import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLang, t, tx  } from '../context/LanguageContext';
import translations from '../i18n/translations';
import { Send, CheckCircle, Loader2, Briefcase, TrendingUp, Users } from 'lucide-react';
import SEO from '../components/SEO';
import { SEO_TEXTES } from '../data/seoTextes';


const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const PREFIXES = ['M.', 'Mme', 'Mlle', 'Dr', 'Prof.', 'Rev.'];

export default function ApporteurAffaires() {
  const { lang } = useLang();
  const a = translations.apporteur;
  const [form, setForm] = useState({
    prefix: 'M.',
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    date_naissance: '',
    adresse: '',
    adresse2: '',
    ville: '',
    code_postal: '',
    pays: 'France',
    comment_connu: '',
  });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const res = await fetch(`/api/apporteur`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, lang }),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ prefix: 'M.', prenom: '', nom: '', email: '', telephone: '', date_naissance: '', adresse: '', adresse2: '', ville: '', code_postal: '', pays: 'France', comment_connu: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputClass = "w-full bg-black/20 border border-white/10 focus:border-[#0099D6] rounded-lg p-4 text-white placeholder-slate-500 focus:ring-1 focus:ring-[#0099D6] transition-all outline-none";
  const labelClass = "block text-sm font-medium text-slate-300 mb-2";

  return (
    <>
      <SEO {...SEO_TEXTES['/devenir-apporteur-daffaires']} path="/devenir-apporteur-daffaires" />
    <main data-testid="apporteur-page">
      {/* Hero */}
      <section className="pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="text-sm font-medium tracking-[0.2em] uppercase text-[#0099D6] mb-4">MÉTÉRIS</p>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6">{t(a.title, lang)}</h1>
            <p className="text-xl text-slate-300 max-w-2xl">{t(a.subtitle, lang)}</p>
          </motion.div>
        </div>
      </section>

      {/* Avantages */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="bg-[#15171E]/80 border border-white/5 rounded-2xl p-6">
              <Briefcase className="w-8 h-8 text-[#0099D6] mb-4" />
              <h3 className="text-white font-semibold mb-2">{t(a.advantage1_title, lang)}</h3>
              <p className="text-slate-400 text-sm">{t(a.advantage1_desc, lang)}</p>
            </div>
            <div className="bg-[#15171E]/80 border border-white/5 rounded-2xl p-6">
              <TrendingUp className="w-8 h-8 text-[#0099D6] mb-4" />
              <h3 className="text-white font-semibold mb-2">{t(a.advantage2_title, lang)}</h3>
              <p className="text-slate-400 text-sm">{t(a.advantage2_desc, lang)}</p>
            </div>
            <div className="bg-[#15171E]/80 border border-white/5 rounded-2xl p-6">
              <Users className="w-8 h-8 text-[#0099D6] mb-4" />
              <h3 className="text-white font-semibold mb-2">{t(a.advantage3_title, lang)}</h3>
              <p className="text-slate-400 text-sm">{t(a.advantage3_desc, lang)}</p>
            </div>
          </motion.div>

          {/* Formulaire */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <motion.div {...fadeUp} className="lg:col-span-3">
              {status === 'success' ? (
                <div data-testid="apporteur-success" className="bg-[#15171E]/80 backdrop-blur-xl border border-emerald-500/20 rounded-2xl p-10 text-center">
                  <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-white mb-3">{t(a.success_title, lang)}</h3>
                  <p className="text-slate-300">{t(a.success_text, lang)}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="apporteur-form">
                  <h2 className="text-2xl font-bold text-white mb-6">{t(a.form_title, lang)}</h2>

                  {/* Préfixe + Prénom + Nom */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>{t(a.prefix, lang)}</label>
                      <select name="prefix" value={form.prefix} onChange={handleChange} className={inputClass}>
                        {PREFIXES.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>{t(a.prenom, lang)} *</label>
                      <input type="text" name="prenom" value={form.prenom} onChange={handleChange} required className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>{t(a.nom, lang)} *</label>
                      <input type="text" name="nom" value={form.nom} onChange={handleChange} required className={inputClass} />
                    </div>
                  </div>

                  {/* Email + Téléphone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelClass}>{t(a.email, lang)} *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} required className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>{t(a.telephone, lang)} *</label>
                      <input type="tel" name="telephone" value={form.telephone} onChange={handleChange} required className={inputClass} />
                    </div>
                  </div>

                  {/* Date de naissance */}
                  <div>
                    <label className={labelClass}>{t(a.date_naissance, lang)}</label>
                    <input type="date" name="date_naissance" value={form.date_naissance} onChange={handleChange} className={inputClass} />
                  </div>

                  {/* Adresse */}
                  <div>
                    <label className={labelClass}>{t(a.adresse, lang)}</label>
                    <input type="text" name="adresse" value={form.adresse} onChange={handleChange} placeholder={tx(lang, 'Adresse postale', 'Street address', { es: 'Dirección postal', de: 'Postanschrift', it: 'Indirizzo postale', pt: 'Endereço postal' })} className={inputClass} />
                  </div>
                  <div>
                    <input type="text" name="adresse2" value={form.adresse2} onChange={handleChange} placeholder={tx(lang, 'Complément d\'adresse', 'Address line 2', { es: 'Dirección adicional', de: 'Zusatzadresse', it: 'Indirizzo aggiuntivo', pt: 'Endereço adicional' })} className={inputClass} />
                  </div>

                  {/* Ville + CP + Pays */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>{t(a.ville, lang)}</label>
                      <input type="text" name="ville" value={form.ville} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>{t(a.code_postal, lang)}</label>
                      <input type="text" name="code_postal" value={form.code_postal} onChange={handleChange} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>{t(a.pays, lang)}</label>
                      <input type="text" name="pays" value={form.pays} onChange={handleChange} className={inputClass} />
                    </div>
                  </div>

                  {/* Comment connu */}
                  <div>
                    <label className={labelClass}>{t(a.comment_connu, lang)}</label>
                    <textarea name="comment_connu" value={form.comment_connu} onChange={handleChange} rows={3} className={`${inputClass} resize-none`} />
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-4 px-8 rounded-lg shadow-[0_0_20px_rgba(0,153,214,0.3)] transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    {t(a.submit, lang)}
                  </button>
                  {status === 'error' && (
                    <p className="text-red-400 text-sm mt-2">{tx(lang, 'Erreur. Veuillez réessayer.', 'Error. Please try again.', { es: 'Error. Por favor, inténtalo de nuevo.', de: 'Fehler. Bitte versuchen Sie es noch mal.', it: 'Errore. Per favore riprovate.', pt: 'Erro. Por favor, tente novamente.' })}</p>
                  )}
                </form>
              )}
            </motion.div>

            {/* Sidebar */}
            <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }} className="lg:col-span-2 space-y-6">
              <div className="bg-[#15171E]/80 backdrop-blur-xl border border-white/5 rounded-2xl p-8">
                <h3 className="text-lg font-semibold text-white mb-4">{t(a.who_title, lang)}</h3>
                <ul className="space-y-3">
                  {[a.who1, a.who2, a.who3, a.who4].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                      <span className="text-[#0099D6] mt-0.5">▸</span>
                      {t(item, lang)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#0099D6]/5 border border-[#0099D6]/20 rounded-2xl p-8">
                <p className="text-slate-300 text-sm leading-relaxed">{t(a.info_note, lang)}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
    </>
  );
}
